import { apm } from '@elastic/apm-rum'

const MAX_DATA_LENGTH = 5000
const REDACTED_FIELDS = ['authorization', 'cookie', 'token', 'password', 'secret']
const API_ERROR = 'ApiError'

export const sanitizeData = (data) => {
  if (typeof data === 'string') return data
  try {
    return JSON.stringify(data) || '[empty]'
  } catch {
    return '[non-serializable data]'
  }
}

export const truncate = (str, max = MAX_DATA_LENGTH) =>
  typeof str === 'string' && str.length > max ? `${str.substring(0, max)}...[truncated]` : str

export const filterSensitive = (obj) =>
  obj && typeof obj === 'object'
    ? Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
          key,
          REDACTED_FIELDS.includes(key.toLowerCase()) ? '[redacted]' : value,
        ]),
      )
    : obj

// проверяем был ли запрос отменён до получения ответа от сервера
// отлавливаем все возможные варианты отмены в axios
export function isCanceledAxiosError(error) {
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.message === 'canceled' ||
    error?.message === 'canceled by user'
  )
}

// проверяем отменился ли запрос по истечению timeout
export const isTimeoutAxiosError = (error) => {
  return error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')
}

// классифицируем http ошибки по статусу
export const getErrorGroup = (error) => {
  if (isCanceledAxiosError(error)) return 'CANCELED'

  if (isTimeoutAxiosError(error)) return 'TIMEOUT'

  if (!error?.response) return 'NETWORK_ERROR'

  const status = error.response.status

  if (status >= 500) return 'SERVER_ERROR'
  if (status === 401) return 'AUTH_ERROR'
  if (status === 404) return 'NOT_FOUND'
  if (status === 403) return 'FORBIDDEN'
  if (status === 400) return 'BAD_REQUEST'

  return `HTTP_${status}`
}

export const initInterceptorsApm = (apiClient) => {
  // создаём apm транзакцию для каждого запроса
  apiClient.interceptors.request.use((config) => {
    const { method = 'get', url = '', headers = {}, params, data } = config

    const shortUrl = url.split('?')?.[0] || url
    const transaction = apm.startTransaction(`API ${method?.toUpperCase()} ${shortUrl}`, 'request')

    // добавляем контекст запроса
    apm.addLabels({
      'http.url': url,
      'http.method': method?.toUpperCase(),
      'request.headers': truncate(sanitizeData(filterSensitive(headers))),
      'request.params': truncate(sanitizeData(params)),
      'request.data': truncate(sanitizeData(data)),
    })

    config.apmTransaction = transaction
    config.shortUrl = shortUrl

    return config
  })

  // обработка ответов и ошибок
  apiClient.interceptors.response.use(
    // при успешном ответе завершаем транзакцию
    (response) => {
      const { config } = response

      // фиксирует время выполнения
      config?.apmTransaction?.end()

      return response
    },
    // при ошибке запроса логируем в apm
    (error) => {
      const { config, message, response } = error || {}
      const { headers: responseHeaders, data: responseData } = response || {}

      // добавляем контекст ответа
      apm.addLabels({
        'response.headers': truncate(sanitizeData(responseHeaders)),
        'response.data': truncate(sanitizeData(responseData)),
      })

      // формируем и отправляем ошибку
      const apmError = new Error(message)
      apmError.name = API_ERROR
      apm.captureError(apmError)

      // завершаем транзакцию (даже при ошибке)
      config?.apmTransaction?.end()

      // перебрасываем ошибку дальше
      return Promise.reject(error)
    },
  )
}
